/* @flow */

import i18nInstance from 'i18next';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import type { ReadOnlyKeyValuePair } from '../jsHelpers/types';
import type { KeyValuePair } from '../jsHelpers/types';

import { logWarning } from '../debug/debug';
import { checkUpdatePageTitle, cleanLanguageList } from './helper';

type TranslationItem = Translation | string;
export type Translation = { [string]: TranslationItem };

class Localizer {
  static instance: typeof i18nInstance | null = null;

  // Application language (determined at startup) as an ISO 2-character alpha code
  static language: string;

  // $FlowFixMe[unsupported-syntax] Flow does not support symbols yet
  get [Symbol.toStringTag]() {
    return 'Localizer';
  }

  static initialize: (language: string, translations: { [string]: TranslationItem }) => Promise<void> = (language, translations) => {
    if (Localizer.instance) {
      return Promise.resolve();
    }

    Localizer.language = language;

    // Ensure that the page title is in the right language
    checkUpdatePageTitle(language);

    const resources: KeyValuePair<{ translation: TranslationItem }> = {};
    Object.entries(translations).forEach(([lang, file]) => {
      resources[lang] = { translation: file };
    });

    return i18nInstance
      .use(initReactI18next)
      .use(Backend)
      .init({
        backend: {
          // Path configuration where async namespaces can be fetched asynchronously if needed
          loadPath: '/locales/{{lng}}/{{ns}}.json',
        },
        debug: false,
        defaultNS: 'translation',
        interpolation: {
          escapeValue: false,
        },
        keySeparator: '.',
        lng: language,
        ns: ['translation'],
        resources, // Pass initial static fallback bundles
      })
      .then(() => {
        Localizer.instance = i18nInstance;
      });
  };

  static getInstance: () => typeof i18nInstance | null = () => Localizer.instance;

  static localize: (key: string, options?: ReadOnlyKeyValuePair<string | number>) => string = (key, options) => {
    if (!Localizer.instance) {
      logWarning(`Localizer.localize called before initialization for key: ${key}`);
      return key;
    }

    return (
      Localizer.instance
        .t(key, options)
        // Replace regular spaces with non-breakable ones when required
        .replace(/ ([:!?-])/giu, '\u00A0$1')
        // Replace underscores with non-breakable spaces
        .replace(/_/giu, '\u00A0')
    );
  };

  /*
   * What this function does:
   * - Takes all languages defined in browser
   * - Only keeps languages supported by application
   * - If resulting list is empty, returns supported languages
   * - Preserves order
   */
  static getAppLanguages: (supportedLanguages: ReadonlyArray<string>) => Array<string> = (supportedLanguages) => {
    const { language, languages } = navigator;
    const browserLanguages = cleanLanguageList(languages && languages.length ? languages : [language]);
    const appLanguages = browserLanguages.filter((lang) => supportedLanguages.includes(lang));

    // Add supported languages if they are missing (not among the browser languages)
    supportedLanguages.forEach((lang) => {
      if (!appLanguages.includes(lang)) {
        appLanguages.push(lang);
      }
    });

    return appLanguages;
  };

  /*
   * Find given key in locales and return, by order of priority:
   * - localized string for current language
   * - default value, if given
   * - first value, if present
   */
  static getLocalizedStringFromAppConf: (locales: ?{ [string]: Array<{ lang: string, value: string }> }, key: string, defaultValue?: string) => string = (locales, key, defaultValue) => {
    let firstValue: string | null = null;

    if (locales) {
      const { [key]: allStrings } = locales;

      if (allStrings && allStrings.length > 0) {
        const localizedString = allStrings.find(({ lang }) => lang === Localizer.language);

        if (localizedString?.value) {
          // Return value for current language
          return localizedString.value;
        }

        firstValue = allStrings[0].value ?? null;
      }
    }

    // Return default value or first value
    return defaultValue || firstValue || `***missing localized string for ${key}***`;
  };

  static addLanguageCode = (params: KeyValuePair<string>): void => {
    params.language = Localizer.language;
  };
}

export default Localizer;
